
import { Vue } from "vue-class-component";
import moment from "moment";
import { Match, Player } from "../models/Models";
import { set, get, del } from "idb-keyval";
import router from '../router';

export default class MatchPlay extends Vue {
  private match: Match = new Match();
  private hasPlayersOnField = false;
  public matchLoaded = false;
  
  beforeCreate() {
    this.match = new Match();
  }

  async created() {
    const id = (this as any).$route.params.id;
    this.match = await get(id);
    this.match.matchPlayedTime = this.getMatchPlayedTime();
    
    this.sortPlayers();

    this.match.players.forEach(p => {
      p._timeOnField = this.getTimeOnField(p);
      p._timeOffField = this.getTimeOffField(p);
      p._timeTotal = this.getTimeTotal(p);
    });

    this.matchLoaded = true;

    setInterval(() => {
      if (this.match.isRunning) {
        this.match.matchPlayedTime = this.getMatchPlayedTime();

        this.match.players.forEach(p => {
          p._timeOnField = this.getTimeOnField(p);
          p._timeOffField = this.getTimeOffField(p);
          p._timeTotal = this.getTimeTotal(p);
        });
      }
    }, 500);
  }

  editMatch(match: Match) {
    router.push({ name: "MatchDetails", params: { id: match.id } });
  }

  async deleteMatch() {
    if (confirm(this.$t('details.confirmDelete'))) {
      if (!this.match) {
        return;
      }
      await del(this.match.id);

      router.push({ name: "Matches"});
    }
  }

  async restartMatch() {
    if (!confirm(this.$t('play.confirmRestart'))) {
      return;
    }

    this.match.players.forEach(p => {
      p.lastStartOnField = undefined;
      p.lastOfField = undefined;
      p._timeOnField = '00:00';
      p._timeOffField = '00:00';
      p._timeTotal = '00:00';
      p.lastSecondsOnField = 0;
      p.lastSecondsOfField = 0;
      p.totalSecondsOnField = 0;
    });

    this.match.lastStarted = undefined;
    this.match.totalSecondsPlayed = 0;
    this.match.matchPlayedTime = '00:00'; 
    this.match.hasStarted = false;
    this.match.homeCount = 0;
    this.match.awayCount = 0;

    this.sortPlayers();

    await this.saveMatch();
  }

  async startMatch() {
    this.match.players.forEach(p => {
      if (p.isOnField) {
        p.lastStartOnField = moment().format("YYYY-MM-DD HH:mm:ss");
      } else {
        p.lastOfField = moment().format("YYYY-MM-DD HH:mm:ss");
      }
    });

    this.match.isRunning = true;
    this.match.lastStarted = moment().format("YYYY-MM-DD HH:mm:ss");
    this.match.hasStarted = true;

    this.sortPlayers();

    await this.saveMatch();
  }

  async stopMatch() {
    this.match.players.forEach(p => {
      if (p.isOnField) {
        const diff = Math.round(
          moment().diff(moment(p.lastStartOnField)) / 1000
        );
        p.lastSecondsOnField += diff;
        p.totalSecondsOnField += diff;
        p.lastStartOnField = undefined;
      } else {
        const diffOfField = Math.round(
          moment().diff(moment(p.lastOfField)) / 1000
        );
        p.lastSecondsOfField += diffOfField;
        p.lastOfField = undefined;
      }
    });

    const timeSinceLastStarted = Math.round(
      moment().diff(this.match.lastStarted) / 1000
    );
    this.match.totalSecondsPlayed =
      this.match.totalSecondsPlayed + timeSinceLastStarted;

    this.match.isRunning = false;

    this.sortPlayers();

    await this.saveMatch();
  }

  async putOnField(player: Player) {
    if (this.match.isRunning) {
      player.lastStartOnField = moment().format("YYYY-MM-DD HH:mm:ss");
    }

    player.isOnField = true;
    
    this.sortPlayers();

    await this.saveMatch();
  }

  private sortPlayers() {
    if (!this.match) {
      return;
    }

    // game has not started yet, order players by on/off field + alphabeth
    if (!this.match.isRunning && this.match.totalSecondsPlayed == 0) {
      const playersOnField = this.match.players
        .filter(x => x.isOnField)
        .sort((a, b) =>
          a.name.localeCompare(b.name)
        );

      const playersOffField = this.match.players
        .filter(x => !x.isOnField)
        .sort((a, b) =>
          a.name.localeCompare(b.name)
        );

      this.hasPlayersOnField = playersOnField.length > 0;    

      this.match.players = playersOnField.concat(playersOffField);

      return;
    }

    // game has started, order by on/off field + time on field/bench
    const playersOnField = this.match.players
      .filter(x => x.isOnField)
      .sort((a, b) => {
        if (a.lastStartOnField && b.lastStartOnField) {
            const diff = 
              (b.lastSecondsOnField + Math.round(moment().diff(b.lastStartOnField) / 1000)) - 
              (a.lastSecondsOnField + Math.round(moment().diff(a.lastStartOnField) / 1000));
            
            return diff == 0 ? a.name.localeCompare(b.name) : a.lastSecondsOnField - b.lastSecondsOnField > 0 ? -1 : 1
        } else {
          return a.lastSecondsOnField - b.lastSecondsOnField == 0 ? a.name.localeCompare(b.name) : a.lastSecondsOnField - b.lastSecondsOnField > 0 ? -1 : 1
        }
      });

    const playersOffField = this.match.players
      .filter(x => !x.isOnField)
      .sort((a, b) => {
        if (a.lastOfField && b.lastOfField) {
          const diff = 
            (b.lastSecondsOfField + Math.round(moment().diff(b.lastOfField) / 1000)) - 
            (a.lastSecondsOfField + Math.round(moment().diff(a.lastOfField) / 1000));
          return diff == 0 ? a.name.localeCompare(b.name) : diff > 0 ? 1 : -1         
        } else {
          return b.lastSecondsOfField - a.lastSecondsOfField == 0 ? a.name.localeCompare(b.name) : b.lastSecondsOfField - a.lastSecondsOfField > 0 ? 1 : -1 
        }
      });

    this.hasPlayersOnField = playersOnField.length > 0;

    this.match.players = playersOnField.concat(playersOffField);
  }

  getFormattedTime(seconds: number) {
    if (seconds == undefined) {
      return "";
    }

    const minutePart = Math.floor(seconds / 60);
    const secondPart = seconds - 60 * minutePart;

    return (
      minutePart.toString().padStart(2, "0") +
      ":" +
      secondPart.toString().padStart(2, "0")
    );
  }

  async takeOfField(player: Player) {
    const diffSeconds = Math.round(
      moment().diff(player.lastStartOnField) / 1000
    );
    player.totalSecondsOnField += diffSeconds;

    player.lastSecondsOnField = 0;
    player.lastSecondsOfField = 0;
    player.lastStartOnField = undefined;
    if (this.match.isRunning) {
      player.lastOfField = moment().format("YYYY-MM-DD HH:mm:ss");
    }
    player.isOnField = false;
    
    this.sortPlayers();

    await this.saveMatch();
  }

  getMatchPlayedTime() {
    if (!this.match) {
      return "";
    }

    if (!this.match.isRunning) {
      return this.match.matchPlayedTime;
    }

    const timeSinceLastStarted = Math.round(
      moment().diff(this.match.lastStarted) / 1000
    );

    const t = this.getFormattedTime(
      timeSinceLastStarted + this.match.totalSecondsPlayed
    );

    return t;
  }

  getTimeOnField(player: Player) {
    if (!player.isOnField) {
      return "00:00";
    }

    if (
      !this.match.isRunning &&
      player.isOnField &&
      player.lastSecondsOnField
    ) {
      return this.getFormattedTime(player.lastSecondsOnField);
    }

    const v = Math.round(moment().diff(player.lastStartOnField) / 1000);
    const t = this.getFormattedTime(v + player.lastSecondsOnField);
    return t;
  }

  getTimeOffField(player: Player) {
    if (player.isOnField) {
      return "00:00";
    } 

    if (!this.match.isRunning &&
      !player.isOnField &&
      player.lastSecondsOfField
    ) {
      return this.getFormattedTime(player.lastSecondsOfField);
    }

    const v = Math.round(moment().diff(player.lastOfField) / 1000);
    const t = this.getFormattedTime(v + player.lastSecondsOfField);
    return t;
  }

  getTimeTotal(player: Player) {
    const v = Math.round(moment().diff(player.lastStartOnField) / 1000);
    const t = this.getFormattedTime(v + player.totalSecondsOnField);

    return t;
  }

  async setHomeCount(newCount: number) {
    this.match.homeCount = newCount;
    await this.saveMatch();
  }

  async setAwayCount(newCount: number) {
    this.match.awayCount = newCount;
    await this.saveMatch();
  }

  async saveMatch() {
    const match = JSON.parse(JSON.stringify(this.match));
    await set(match.id, match);
  }
}
